import type { DBSchema, IDBPDatabase } from "idb";
import { openDB } from "idb";

interface PreviewData {
  tempId: string;
  preview: string;
  formStateHash: string;
  timestamp: number;
  templateId: string;
}

interface FormPreviewDB extends DBSchema {
  previews: {
    key: string;
    value: PreviewData;
  };
}

const DB_NAME = "FormPreviewCache";
const DB_VERSION = 1;
const STORE_NAME = "previews";
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

let dbPromise: Promise<IDBPDatabase<FormPreviewDB>> | null = null;

/**
 * Initialize or get the IndexedDB database instance
 */
const getDB = async (): Promise<IDBPDatabase<FormPreviewDB>> => {
  if (!dbPromise) {
    dbPromise = openDB<FormPreviewDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "tempId" });
        }
      },
    });
  }
  return dbPromise;
};

/**
 * Generate a simple hash of the form state for comparison
 */
const generateFormStateHash = (formState: any): string => {
  try {
    return JSON.stringify(formState);
  } catch {
    return "";
  }
};

/**
 * Save preview data to IndexedDB
 */
export const savePreviewToCache = async (
  tempId: string,
  preview: string,
  formState: any,
  templateId: string
): Promise<void> => {
  try {
    const db = await getDB();
    const formStateHash = generateFormStateHash(formState);

    await db.put(STORE_NAME, {
      tempId,
      preview,
      formStateHash,
      timestamp: Date.now(),
      templateId,
    });
  } catch (error) {
    console.warn("Failed to save preview to cache:", error);
    // Fail silently - caching is an enhancement, not a requirement
  }
};

/**
 * Get preview data from IndexedDB if it exists and is valid
 */
export const getPreviewFromCache = async (
  tempId: string,
  formState: any
): Promise<string | null> => {
  try {
    const db = await getDB();
    const cached = await db.get(STORE_NAME, tempId);

    if (!cached) {
      return null;
    }

    // Check if cache is expired
    const isExpired = Date.now() - cached.timestamp > CACHE_EXPIRY_MS;
    if (isExpired) {
      await clearPreviewFromCache(tempId);
      return null;
    }

    // Check if form state matches
    const currentFormStateHash = generateFormStateHash(formState);
    if (cached.formStateHash !== currentFormStateHash) {
      // Form state has changed, cache is invalid
      return null;
    }

    return cached.preview;
  } catch (error) {
    console.warn("Failed to get preview from cache:", error);
    return null;
  }
};

/**
 * Clear a specific preview from cache
 */
export const clearPreviewFromCache = async (tempId: string): Promise<void> => {
  try {
    const db = await getDB();
    await db.delete(STORE_NAME, tempId);
  } catch (error) {
    console.warn("Failed to clear preview from cache:", error);
  }
};

/**
 * Clear all expired previews from cache
 */
export const clearExpiredPreviews = async (): Promise<void> => {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const allKeys = await store.getAllKeys();

    const now = Date.now();

    for (const key of allKeys) {
      const item = await store.get(key);
      if (item && now - item.timestamp > CACHE_EXPIRY_MS) {
        await store.delete(key);
      }
    }

    await tx.done;
  } catch (error) {
    console.warn("Failed to clear expired previews:", error);
  }
};

/**
 * Clear all previews from cache
 */
export const clearAllPreviews = async (): Promise<void> => {
  try {
    const db = await getDB();
    await db.clear(STORE_NAME);
  } catch (error) {
    console.warn("Failed to clear all previews:", error);
  }
};
